import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  Info,
  MapPin,
  Phone,
  Plane,
} from 'lucide-react';
import DashboardLayout from '../components/Layout/DashboardLayout';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import ConfirmModal from '../components/UI/ConfirmModal';
import DatePickerField from '../components/UI/DatePickerField';
import FormSelect from '../components/UI/FormSelect';
import PhoneNumberInput from '../components/UI/PhoneNumberInput';
import { StatusBadge } from '../components/UI/StatusBadge';
import { CountBadge } from '../components/UI/CountBadge';
import { DepartmentTradeChips } from '../components/UI/DepartmentTradeChips';
import { LeaveTypeBadge } from '../components/UI/LeaveTypeBadge';
import {
  DATE_PICKER_DATETIME_FORMAT,
  DATE_PICKER_FORMAT,
  formatDate,
  formatDateRange,
  toLocalISODate,
} from '../utils/dateUtils';
import {
  useCancelTravelNotice,
  useConfirmTravelNoticeReturn,
  useCreateTravelNotice,
  useMyActiveTravelNotices,
  useTravelNoticeBootstrap,
  useTravelNoticeConcurrentAbsence,
  useTravelNoticeWindowPreview,
} from '../hooks/useTravelNotices';

const toIsoOrNull = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return `${formatDate(date)} · ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const defaultRangeEnd = (start) => {
  const end = new Date(start);
  end.setDate(end.getDate() + 7);
  return end;
};

const sourcePeriod = (source) => {
  if (source.kind === 'travel_notice') {
    return {
      start: new Date(source.departure_at),
      end: new Date(source.expected_return_at),
    };
  }

  const start = new Date(`${source.leave_start_date}T00:00:00`);
  const end = new Date(`${source.leave_end_date}T23:59:59.999`);
  return { start, end };
};

const AbsenceSourceChip = ({ source }) => {
  if (source.kind === 'leave') {
    return <LeaveTypeBadge leaveTypeName={source.leave_type} />;
  }
  if (source.status === 'overdue') {
    return <StatusBadge status="overdue" label="Travel notice · overdue" />;
  }
  if (source.status === 'returned') {
    return <StatusBadge status="returned" label="Travel notice · returned" />;
  }
  return <StatusBadge status="travel_notice" />;
};

const buildTravelEpisodes = (rows) => {
  const uniqueSources = new Map();

  rows.forEach((row) => {
    (row.sources || []).forEach((source) => {
      const sourceKey = `${row.staff_id}:${source.kind}:${source.id}`;
      if (uniqueSources.has(sourceKey)) return;
      const period = sourcePeriod(source);
      if (Number.isNaN(period.start.getTime()) || Number.isNaN(period.end.getTime())) return;
      uniqueSources.set(sourceKey, {
        ...period,
        staff_id: row.staff_id,
        worker_name: row.worker_name,
        department: row.department,
        trade_code: row.trade_code,
        trade_name: row.trade_name,
        sources: [source],
      });
    });
  });

  const byWorker = new Map();
  uniqueSources.forEach((episode) => {
    const workerEpisodes = byWorker.get(episode.staff_id) || [];
    workerEpisodes.push(episode);
    byWorker.set(episode.staff_id, workerEpisodes);
  });

  const merged = [];
  byWorker.forEach((workerEpisodes) => {
    workerEpisodes
      .sort((a, b) => a.start - b.start)
      .forEach((episode) => {
        const current = merged[merged.length - 1];
        if (
          current
          && current.staff_id === episode.staff_id
          && episode.start <= current.end
        ) {
          current.start = new Date(Math.min(current.start.getTime(), episode.start.getTime()));
          current.end = new Date(Math.max(current.end.getTime(), episode.end.getTime()));
          current.sources.push(...episode.sources);
          return;
        }
        merged.push({ ...episode, sources: [...episode.sources] });
      });
  });

  return merged.sort((a, b) => a.start - b.start || a.worker_name.localeCompare(b.worker_name));
};

const ActiveNoticeCard = ({ notice, nextShiftIso }) => {
  const [confirmAction, setConfirmAction] = useState(null);
  const cancelMutation = useCancelTravelNotice();
  const returnMutation = useConfirmTravelNoticeReturn();
  const pending = cancelMutation.isPending || returnMutation.isPending;

  const returnAfterShift = useMemo(() => {
    if (!nextShiftIso || !notice.expected_return_at) return false;
    return new Date(notice.expected_return_at) > new Date(nextShiftIso);
  }, [notice.expected_return_at, nextShiftIso]);

  const destination = notice.destination_city
    ? `${notice.destination_city}, ${notice.destination_country}`
    : notice.destination_country;

  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      <div className="p-4 sm:p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Your travel notice
            </p>
            <h2 className="text-card-header mt-1 truncate">{destination}</h2>
            <p className="text-sm text-gray-600 mt-1">
              {formatDateTime(notice.departure_at)} → {formatDateTime(notice.expected_return_at)}
            </p>
          </div>
          <StatusBadge status={notice.status} label={notice.status_display} />
        </div>

        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-gray-100">
          <div>
            <dt className="text-xs text-gray-500">Contact while overseas</dt>
            <dd className="text-sm font-medium text-gray-900">{notice.overseas_contact_number}</dd>
          </div>
          {nextShiftIso && (
            <div>
              <dt className="text-xs text-gray-500">Your next shift</dt>
              <dd className="text-sm font-medium text-gray-900">{formatDateTime(nextShiftIso)}</dd>
            </div>
          )}
        </dl>

        {returnAfterShift && notice.status !== 'overdue' && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-950">Return is after your next shift</p>
                <p className="text-sm text-amber-900 mt-1">
                  Apply for leave if you will miss work, or update your return time.
                </p>
              </div>
            </div>
          </div>
        )}

        {notice.status === 'overdue' && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-950">Return is overdue</p>
                <p className="text-sm text-amber-900 mt-1">
                  Confirm your return now, or contact HR if you are delayed.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button
            type="button"
            onClick={() => setConfirmAction('cancel')}
            disabled={pending}
            className="btn btn-outline w-full sm:w-auto"
          >
            Cancel notice
          </button>
          <button
            type="button"
            onClick={() => setConfirmAction('return')}
            disabled={pending}
            className="btn btn-primary w-full sm:w-auto"
          >
            <CheckCircle2 className="w-4 h-4" />
            I have returned to Singapore
          </button>
        </div>
      </div>

      <ConfirmModal
        isOpen={confirmAction === 'return'}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() => {
          returnMutation.mutate(notice.id, {
            onSuccess: () => setConfirmAction(null),
          });
        }}
        title="Confirm you have returned?"
        confirmLabel="Yes, I am back"
        variant="primary"
        theme="employee"
        isLoading={returnMutation.isPending}
      >
        <p className="text-sm text-gray-700">
          Confirm only after you are back in Singapore.
        </p>
      </ConfirmModal>

      <ConfirmModal
        isOpen={confirmAction === 'cancel'}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() => {
          cancelMutation.mutate(notice.id, {
            onSuccess: () => setConfirmAction(null),
          });
        }}
        title="Cancel this travel notice?"
        confirmLabel="Cancel notice"
        variant="danger"
        theme="employee"
        isLoading={cancelMutation.isPending}
      >
        <p className="text-sm text-gray-700">
          Cancel only if you are no longer travelling on these dates.
        </p>
      </ConfirmModal>
    </div>
  );
};

const TravelNoticeForm = ({ bootstrap }) => {
  const createMutation = useCreateTravelNotice();
  const [pickId, setPickId] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [departureAt, setDepartureAt] = useState(null);
  const [returnAt, setReturnAt] = useState(null);
  const [contact, setContact] = useState(bootstrap?.profile_mobile || '');
  const [editingContact, setEditingContact] = useState(!bootstrap?.profile_mobile);

  const quickPicks = bootstrap?.quick_picks || [];
  const countries = (bootstrap?.countries || []).map((item) => ({
    value: item,
    label: item,
  }));

  const departureIso = toIsoOrNull(departureAt);
  const returnIso = toIsoOrNull(returnAt);
  const windowValid = Boolean(
    departureIso && returnIso && new Date(returnIso) > new Date(departureIso),
  );

  const { data: windowPreview } = useTravelNoticeWindowPreview(
    { departure_at: departureIso, expected_return_at: returnIso },
    { enabled: windowValid },
  );

  const nextShiftIso = windowPreview?.next_shift_iso || bootstrap?.next_shift_iso;
  const returnAfterShift = Boolean(windowPreview?.return_after_next_shift);
  const noRestOrPh = windowValid && windowPreview?.includes_rest_or_ph === false;

  const selectPick = (pick) => {
    const id = `${pick.city}|${pick.country}`;
    setPickId(id);
    setCountry(pick.country);
    setCity(pick.city);
  };

  const selectOther = () => {
    setPickId('other');
    setCountry('');
    setCity('');
  };

  const canSubmit = Boolean(country && departureIso && returnIso && windowValid && contact);

  const handleSubmit = () => {
    createMutation.mutate({
      destination_country: country,
      destination_city: city,
      departure_at: departureIso,
      expected_return_at: returnIso,
      overseas_contact_number: contact,
    });
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      <div className="p-4 sm:p-5 border-b border-blue-100 bg-blue-50">
        <div className="flex items-start gap-3">
          <Info className="w-6 h-6 text-blue-700 flex-shrink-0" />
          <div>
            <h2 className="text-card-header text-blue-950">No approval needed</h2>
            <p className="text-sm text-blue-800 mt-1">
              This is a notice only. Tell HR where you are going so we can contact you.
              You do not need permission to travel on your rest day or day off.
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-5 space-y-5">
        <div>
          <p className="form-label">
            Where are you going? <span className="text-red-500">*</span>
          </p>
          <div className="flex flex-wrap gap-2">
            {quickPicks.map((pick) => {
              const id = `${pick.city}|${pick.country}`;
              const active = pickId === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => selectPick(pick)}
                  className={`btn btn-sm ${active ? 'btn-primary' : 'btn-outline'}`}
                >
                  <MapPin className="w-3.5 h-3.5" />
                  {pick.city}
                </button>
              );
            })}
            <button
              type="button"
              onClick={selectOther}
              className={`btn btn-sm ${pickId === 'other' ? 'btn-primary' : 'btn-outline'}`}
            >
              Other
            </button>
          </div>
        </div>

        {pickId === 'other' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="form-group">
              <label className="form-label">
                Country <span className="text-red-500">*</span>
              </label>
              <FormSelect
                options={countries}
                value={country}
                onChange={setCountry}
                placeholder="Select country…"
                theme="employee"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">
                City <span className="text-gray-400 font-normal ml-1">(optional)</span>
              </label>
              <input
                className="input"
                value={city}
                onChange={(event) => setCity(event.target.value)}
                placeholder="City or town"
              />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="form-group">
            <label className="form-label">
              Leaving date & time <span className="text-red-500">*</span>
            </label>
            <DatePickerField
              selected={departureAt}
              onChange={setDepartureAt}
              showTimeSelect
              timeIntervals={15}
              dateFormat={DATE_PICKER_DATETIME_FORMAT}
              theme="employee"
              placeholderText="Select date & time"
            />
          </div>
          <div className="form-group">
            <label className="form-label">
              Return date & time <span className="text-red-500">*</span>
            </label>
            <DatePickerField
              selected={returnAt}
              onChange={setReturnAt}
              showTimeSelect
              timeIntervals={15}
              dateFormat={DATE_PICKER_DATETIME_FORMAT}
              theme="employee"
              placeholderText="Select date & time"
              minDate={departureAt || undefined}
            />
          </div>
        </div>

        <div className="form-group">
          <div className="flex items-center justify-between gap-3 mb-2">
            <label className="form-label mb-0">
              Contact number while overseas <span className="text-red-500">*</span>
            </label>
            {bootstrap?.profile_mobile && (
              <button
                type="button"
                onClick={() => {
                  if (editingContact) {
                    setContact(bootstrap.profile_mobile);
                    setEditingContact(false);
                    return;
                  }
                  setEditingContact(true);
                }}
                className="text-sm font-medium text-blue-800 hover:text-blue-900 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded"
              >
                {editingContact ? 'Use profile number' : 'Change'}
              </button>
            )}
          </div>

          {!editingContact && bootstrap?.profile_mobile ? (
            <>
              <p className="field-static gap-2.5" aria-live="polite">
                <Phone className="w-4 h-4 text-blue-800 shrink-0" aria-hidden="true" />
                <span className="font-medium text-gray-900 tabular-nums tracking-wide">
                  {contact}
                </span>
              </p>
              <p className="form-help">
                From your profile. We will contact you on this number while you are overseas.
              </p>
            </>
          ) : (
            <>
              <PhoneNumberInput
                name="overseas_contact_number"
                value={contact}
                onChange={(event) => setContact(event.target.value)}
                required
                defaultCountry="SG"
              />
              <p className="form-help">
                Enter the number we can reach you on while overseas.
              </p>
            </>
          )}
        </div>

        {noRestOrPh && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900">
                This notice is for personal-time travel. If you will miss work, apply for leave as well.
              </p>
            </div>
          </div>
        )}

        {returnAfterShift && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-950">
                  Your return is after your next shift
                </p>
                <p className="text-sm text-amber-900 mt-1">
                  You can still submit this notice. If you will miss work, apply for leave as well.
                  {nextShiftIso ? ` Next shift: ${formatDateTime(nextShiftIso)}.` : ''}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-900">
              Company medical insurance covers treatment in Singapore only.
              Consider buying travel insurance before you leave, even for short trips.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit || createMutation.isPending}
          className="btn btn-primary w-full disabled:opacity-50"
        >
          <Plane className="w-4 h-4" />
          {createMutation.isPending ? 'Submitting…' : 'Submit travel notice'}
        </button>
      </div>
    </div>
  );
};

const TeamAbsenceView = () => {
  const [startDate, setStartDate] = useState(() => new Date());
  const [endDate, setEndDate] = useState(() => defaultRangeEnd(new Date()));

  const startIso = toLocalISODate(startDate);
  const endIso = toLocalISODate(endDate);
  const rangeValid = Boolean(startIso && endIso && startIso <= endIso);

  const { data: rows = [], isLoading } = useTravelNoticeConcurrentAbsence(
    { start_date: startIso, end_date: endIso },
    { enabled: rangeValid },
  );

  const sections = useMemo(() => {
    const now = new Date();
    const grouped = {
      current: [],
      upcoming: [],
      earlier: [],
    };

    buildTravelEpisodes(rows).forEach((episode) => {
      const notices = episode.sources.filter((source) => source.kind === 'travel_notice');
      const returned = notices.length > 0
        && notices.every((source) => source.status === 'returned');
      if (returned || episode.end < now) {
        grouped.earlier.push(episode);
      } else if (episode.start > now) {
        grouped.upcoming.push(episode);
      } else {
        grouped.current.push(episode);
      }
    });

    return [
      {
        key: 'current',
        title: 'Overseas now',
        description: 'Team members currently outside Singapore.',
        items: grouped.current,
      },
      {
        key: 'upcoming',
        title: 'Upcoming travel',
        description: 'Overseas periods starting within the selected dates.',
        items: grouped.upcoming,
      },
      {
        key: 'earlier',
        title: 'Earlier in range',
        description: 'Travel periods already completed within the selected dates.',
        items: grouped.earlier,
      },
    ].filter((section) => section.items.length > 0);
  }, [rows]);

  const handleStartChange = (next) => {
    if (!next) return;
    setStartDate(next);
    if (endDate && toLocalISODate(next) > toLocalISODate(endDate)) {
      setEndDate(next);
    }
  };

  const handleEndChange = (next) => {
    if (!next) return;
    setEndDate(next);
    if (startDate && toLocalISODate(next) < toLocalISODate(startDate)) {
      setStartDate(next);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-5 space-y-4">
        <div>
          <h2 className="text-card-header">Team travel overview</h2>
          <p className="text-sm text-gray-600 mt-1">
            One entry per overseas period. Approved Home and Urgent Leave are included
            automatically, so employees do not need to submit the same trip twice.
          </p>
        </div>
        <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-end gap-2 sm:gap-3">
          <div className="form-group min-w-0">
            <label className="form-label">From</label>
            <DatePickerField
              selected={startDate}
              onChange={handleStartChange}
              dateFormat={DATE_PICKER_FORMAT}
              theme="employee"
              placeholderText="Select date"
              maxDate={endDate || undefined}
            />
          </div>
          <span className="text-gray-400 pb-3" aria-hidden="true">→</span>
          <div className="form-group min-w-0">
            <label className="form-label">To</label>
            <DatePickerField
              selected={endDate}
              onChange={handleEndChange}
              dateFormat={DATE_PICKER_FORMAT}
              theme="employee"
              placeholderText="Select date"
              minDate={startDate || undefined}
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <LoadingSpinner />
      ) : sections.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border">
          <Plane className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No team travel</h3>
          <p className="text-sm text-gray-500">No overseas periods overlap the selected dates.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {sections.map((section) => (
            <section key={section.key} aria-labelledby={`travel-${section.key}`}>
              <div className="mb-3">
                <h2
                  id={`travel-${section.key}`}
                  className="text-card-header flex items-center min-w-0 gap-2"
                >
                  <span className="truncate">{section.title}</span>
                  <CountBadge
                    count={section.items.length}
                    theme="employee"
                    size="md"
                    className="flex-shrink-0"
                  />
                </h2>
                <p className="text-sm text-gray-500 mt-0.5">{section.description}</p>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                {section.items.map((episode) => {
                  const travelSources = episode.sources.filter(
                    (source) => source.kind === 'travel_notice',
                  );
                  const leaveSources = episode.sources.filter(
                    (source) => source.kind === 'leave',
                  );
                  const destinations = [...new Set(
                    travelSources.map((source) => source.destination).filter(Boolean),
                  )];
                  const hasTravelNotice = travelSources.length > 0;
                  const displayStart = hasTravelNotice
                    ? new Date(Math.min(
                      ...travelSources.map((source) => new Date(source.departure_at).getTime()),
                    ))
                    : episode.start;
                  const displayEnd = hasTravelNotice
                    ? new Date(Math.max(
                      ...travelSources.map((source) => new Date(source.expected_return_at).getTime()),
                    ))
                    : episode.end;
                  const periodLabel = formatDateRange(displayStart, displayEnd);
                  const title = destinations.length > 0
                    ? destinations.join(' · ')
                    : 'Overseas on approved leave';
                  const uniqueChips = [];
                  const seenChips = new Set();
                  [...leaveSources, ...travelSources].forEach((source) => {
                    const chipKey = source.kind === 'leave'
                      ? `leave:${source.leave_type || ''}`
                      : `travel:${source.status || 'submitted'}`;
                    if (seenChips.has(chipKey)) return;
                    seenChips.add(chipKey);
                    uniqueChips.push(source);
                  });

                  return (
                    <article
                      key={`${episode.staff_id}-${episode.start.toISOString()}`}
                      className="bg-white rounded-2xl shadow-sm border p-4 sm:p-5"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                          <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                            <Plane className="w-5 h-5 text-blue-800" aria-hidden="true" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="text-base font-semibold text-gray-900 truncate" title={title}>
                              {title}
                            </h3>
                            <p className="text-sm text-gray-700 mt-0.5 truncate">
                              {episode.worker_name} · {episode.staff_id}
                            </p>
                            <DepartmentTradeChips
                              department={episode.department}
                              trade={
                                episode.trade_code || episode.trade_name
                                  ? {
                                    trade_code: episode.trade_code,
                                    name: episode.trade_name,
                                  }
                                  : null
                              }
                              layout="inline"
                              size="xs"
                              className="mt-2"
                            />
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          {uniqueChips.map((source) => (
                            <AbsenceSourceChip
                              key={`${source.kind}-${source.id}`}
                              source={source}
                            />
                          ))}
                        </div>
                      </div>

                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="flex items-start gap-2.5">
                          <CalendarDays
                            className="w-4 h-4 text-blue-800 flex-shrink-0 mt-0.5"
                            aria-hidden="true"
                          />
                          <div className="min-w-0">
                            <p className="text-xs text-gray-500">Overseas period</p>
                            <p className="text-sm font-medium text-gray-900 mt-0.5">
                              {periodLabel}
                            </p>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

const SelfServiceTravelNotice = () => {
  const [tab, setTab] = useState('mine');
  const { data: bootstrap, isLoading: bootstrapLoading } = useTravelNoticeBootstrap();
  const { data: activeNotices = [], isLoading: activeLoading } = useMyActiveTravelNotices();
  const activeNotice = activeNotices[0];
  const canViewTeam = !!bootstrap?.can_view_team;

  return (
    <DashboardLayout>
      <div className="space-y-6 bg-gray-50 min-h-screen">
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
          <div className="flex items-start gap-3 sm:gap-4">
            <Link
              to="/self-service"
              aria-label="Back to Self-Service"
              title="Back to Self-Service"
              className="icon-button flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-bold text-gray-900">Overseas Travel Notice</h1>
              <p className="text-sm text-gray-500 mt-1">
                Notify HR before overseas travel on a rest day or day off. No approval required.
              </p>
            </div>
          </div>
        </div>

        {canViewTeam && (
          <div
            role="tablist"
            aria-label="Travel notice views"
            className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth"
          >
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'mine'}
              onClick={() => setTab('mine')}
              className={`tab-btn ${tab === 'mine' ? 'active' : ''}`}
            >
              My notice
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'team'}
              onClick={() => setTab('team')}
              className={`tab-btn ${tab === 'team' ? 'active' : ''}`}
            >
              Team overseas
            </button>
          </div>
        )}

        {(bootstrapLoading || activeLoading) ? (
          <LoadingSpinner />
        ) : tab === 'team' && canViewTeam ? (
          <TeamAbsenceView />
        ) : activeNotice ? (
          <ActiveNoticeCard
            notice={activeNotice}
            nextShiftIso={bootstrap?.next_shift_iso}
          />
        ) : (
          <TravelNoticeForm bootstrap={bootstrap} />
        )}
      </div>
    </DashboardLayout>
  );
};

export default SelfServiceTravelNotice;
